import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Img } from "@/components/Img";
import { WatermarkBanner } from "@/components/Watermark";
import {
  Trophy, MapPin, TrendingUp, Building2, ArrowUpRight, Loader2, Sparkles,
  Bed, Bath, Square,
} from "lucide-react";

function fmtMoney(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1000) return "$" + Math.round(n / 1000).toLocaleString() + "K";
  return "$" + Math.round(n).toLocaleString();
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 86400) return "today";
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / (86400 * 7))}w ago`;
  if (diff < 86400 * 365) return `${Math.round(diff / (86400 * 30))}mo ago`;
  return new Date(iso).toLocaleDateString();
}

export default function PublicWins() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/wins/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <Loader2 size={28} className="animate-spin text-[var(--ink-3)]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="card max-w-md text-center py-12">
          <Trophy size={28} className="text-[var(--ink-3)] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">No wins page here</div>
          <p className="text-[var(--ink-2)] mt-2">This link may be private, expired, or never existed.</p>
          <Link to="/" className="btn-outline mt-5 inline-flex">Visit iSpeedToClose</Link>
        </div>
      </div>
    );
  }

  const { agent, page, stats, deals } = data;
  const accent = agent.brand_accent || "#10B981";

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="public-wins-page">
      {/* Hero */}
      <header
        className="relative overflow-hidden text-white"
        style={{
          background: `linear-gradient(135deg, #0B132B 0%, #1B2440 55%, ${accent}33 100%)`,
        }}
      >
        <div
          className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-40 pointer-events-none"
          style={{ background: `radial-gradient(circle, ${accent}77 0%, transparent 70%)` }}
        />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20 relative">
          {/* Brand row */}
          <div className="flex items-center gap-3 mb-10">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
              style={{ background: agent.brand_logo ? "#fff" : accent, padding: agent.brand_logo ? 4 : 0 }}
            >
              {agent.brand_logo ? (
                <Img src={agent.brand_logo} alt="" className="max-w-full max-h-full object-contain" />
              ) : (
                <Building2 size={20} color="#fff" />
              )}
            </div>
            <div>
              <div className="font-display font-extrabold text-xl leading-none">
                {agent.brand_company || "iSpeedToClose"}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-white/55 mt-1.5">
                Recent wins
              </div>
            </div>
          </div>

          <div className="label-eyebrow mb-3" style={{ color: accent }}>
            <Trophy size={11} className="inline mr-1.5" /> Win Story
          </div>
          <h1
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight"
            data-testid="public-wins-headline"
          >
            {page.headline}
          </h1>
          <p
            className="mt-5 text-lg text-white/70 max-w-2xl"
            data-testid="public-wins-subline"
            style={{ display: page.subline ? undefined : "none" }}
          >
            {page.subline}
          </p>

          <div className="grid sm:grid-cols-4 gap-4 mt-12 max-w-3xl" data-testid="public-wins-stats">
            <HeroStat label="Deals closed" value={stats.deal_count} accent={accent} testid="stat-deal-count" />
            <HeroStat label="Total ARV moved" value={fmtMoney(stats.total_arv)} accent={accent} testid="stat-total-arv" />
            <HeroStat label="Assignment fees" value={fmtMoney(stats.total_fees)} accent={accent} testid="stat-total-fees" />
            <HeroStat
              label="Cities"
              value={(stats.cities || []).length}
              hint={stats.cities?.slice(0, 3).join(", ")}
              accent={accent}
              testid="stat-cities"
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        {deals.length === 0 ? (
          <div className="card text-center py-16">
            <Trophy size={28} className="text-[var(--ink-3)] mx-auto mb-3" />
            <div className="font-display font-bold">No deals published yet — check back soon.</div>
          </div>
        ) : (
          <>
            <div className="label-eyebrow mb-3">The deals</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="public-wins-deal-grid">
              {deals.map((d) => <DealCard key={d.id} d={d} accent={accent} />)}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[var(--line)] mt-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-[var(--ink-2)]">
            Want a story like this on your wall? <Link to="/" className="font-semibold underline">Start with iSpeedToClose.</Link>
          </div>
          <Link to="/" className="btn-outline text-sm">
            <Sparkles size={13} /> Powered by iSpeedToClose <ArrowUpRight size={11} />
          </Link>
        </div>
      </footer>

      {agent.watermark && <WatermarkBanner />}
    </div>
  );
}

function HeroStat({ label, value, hint, accent, testid }) {
  return (
    <div
      className="rounded-xl p-4 backdrop-blur-sm"
      style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}33` }}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-white/55 font-semibold">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1 tabular-nums" style={{ color: accent }}>{value}</div>
      {hint && <div className="text-xs text-white/60 mt-1 truncate">{hint}</div>}
    </div>
  );
}

function DealCard({ d, accent }) {
  return (
    <article
      className="card transition hover:shadow-md"
      style={{ borderColor: "var(--line)" }}
      data-testid={`public-wins-deal-${d.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="inline-flex items-center gap-1.5 text-sm font-bold">
          <MapPin size={13} style={{ color: accent }} />
          <span>{d.city || "—"}{d.state ? `, ${d.state}` : ""}</span>
        </div>
        <span
          className="chip"
          style={{ background: accent + "22", color: accent, borderColor: accent + "55", fontSize: 10 }}
        >
          <span className="chip-dot" style={{ background: accent }} />
          WON
        </span>
      </div>

      {/* Spec line */}
      <div className="flex items-center gap-3 text-xs text-[var(--ink-3)] mb-4">
        {d.bedrooms ? <span className="inline-flex items-center gap-1"><Bed size={11} /> {d.bedrooms}</span> : null}
        {d.bathrooms ? <span className="inline-flex items-center gap-1"><Bath size={11} /> {d.bathrooms}</span> : null}
        {d.sqft ? <span className="inline-flex items-center gap-1"><Square size={11} /> {d.sqft.toLocaleString()} sf</span> : null}
        {d.zip_code && <span className="ml-auto font-mono">{d.zip_code}</span>}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <Mini label="ARV" value={fmtMoney(d.estimated_arv)} />
        <Mini label="Fee" value={fmtMoney(d.assignment_fee)} accent={accent} />
      </div>

      <div className="flex items-center justify-between text-xs pt-3 border-t border-[var(--line)]">
        <span className="text-[var(--ink-3)] flex items-center gap-1">
          <TrendingUp size={11} /> {d.days_to_close === 0 ? "Same-day close" : `Closed in ${d.days_to_close} day${d.days_to_close === 1 ? "" : "s"}`}
        </span>
        <span className="text-[var(--ink-3)]">{timeAgo(d.won_at)}</span>
      </div>
    </article>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className="rounded-lg p-2.5" style={{ background: "rgba(15,23,42,0.04)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">{label}</div>
      <div className="font-display font-bold text-lg tabular-nums mt-0.5" style={{ color: accent || "var(--ink)" }}>{value}</div>
    </div>
  );
}
